import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import translations, { DEFAULT_LANGUAGE, AVAILABLE_LANGUAGES } from '../locales/translations';

const STORAGE_KEY = 'app_language';

const LanguageContext = createContext(null);

/**
 * Resolve a dotted key like "sidebar.fragen" to a string from the given dict.
 * Returns null if any segment is missing so callers can fall back.
 */
const resolveKey = (dict, key) => {
  if (!dict || typeof key !== 'string') return null;
  const parts = key.split('.');
  let node = dict;
  for (const part of parts) {
    if (node && typeof node === 'object' && part in node) {
      node = node[part];
    } else {
      return null;
    }
  }
  return typeof node === 'string' ? node : null;
};

/**
 * Replace {placeholders} in a string with values from the `vars` object.
 * Missing vars are left as-is so issues are visible during QA.
 */
const interpolate = (text, vars) => {
  if (!vars || typeof text !== 'string') return text;
  return text.replace(/\{(\w+)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match
  );
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && translations[stored] ? stored : DEFAULT_LANGUAGE;
  });

  const setLanguage = useCallback((code) => {
    setLanguageState((prev) => {
      const next = translations[code] ? code : DEFAULT_LANGUAGE;
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, next);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  /**
   * Translate a key with optional interpolation variables.
   * Fallback chain: active language -> default language -> key itself.
   */
  const t = useCallback(
    (key, vars) => {
      const active = resolveKey(translations[language], key);
      if (active !== null) return interpolate(active, vars);
      const fallback = resolveKey(translations[DEFAULT_LANGUAGE], key);
      if (fallback !== null) return interpolate(fallback, vars);
      return key;
    },
    [language]
  );

  const value = useMemo(
    () => ({ language, setLanguage, t, availableLanguages: AVAILABLE_LANGUAGES }),
    [language, setLanguage, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

/**
 * Access translation helpers anywhere under <LanguageProvider>.
 * Safe to call outside the provider too — returns a no-op `t` that echoes keys
 * back, which keeps the UI from crashing during partial rollouts.
 */
export const useTranslation = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    return {
      language: DEFAULT_LANGUAGE,
      setLanguage: () => {},
      t: (key) => key,
      availableLanguages: AVAILABLE_LANGUAGES,
    };
  }
  return ctx;
};

export default LanguageContext;
